// confirm-action-dialog.component.ts
import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

export interface ConfirmActionDialogData {
  title: string;
  message: string;
  actionType: 'archive' | 'reactivate' | 'delete' | 'reset';
  itemName: string;
  requiresReason?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export interface ConfirmActionDialogResult {
  confirmed: boolean;
  reason?: string;
}

@Component({
  selector: 'app-confirm-action-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    ReactiveFormsModule
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <mat-icon [class]="'icon-' + data.actionType">
          {{ getIcon() }}
        </mat-icon>
        <h2 mat-dialog-title>{{ data.title }}</h2>
      </div>

      <mat-dialog-content>
        <p class="dialog-message">{{ data.message }}</p>
        
        <div class="item-highlight">
          <strong>{{ data.itemName }}</strong>
        </div>

        <mat-form-field 
          *ngIf="data.requiresReason" 
          appearance="outline" 
          class="full-width">
          <mat-label>Reason {{ data.actionType === 'archive' ? '(Optional)' : '' }}</mat-label>
          <textarea 
            matInput 
            [formControl]="reasonControl"
            rows="3"
            placeholder="Enter reason for this action..."
            maxlength="500">
          </textarea>
          <mat-hint align="end">{{ reasonControl.value?.length || 0 }}/500</mat-hint>
        </mat-form-field>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button 
          mat-stroked-button 
          (click)="onCancel()">
          {{ data.cancelButtonText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button 
          [class]="getButtonClass()"
          (click)="onConfirm()">
          {{ data.confirmButtonText || 'Confirm' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .dialog-container {
      min-width: 400px;
      max-width: 600px;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
    }

    .dialog-header mat-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
    }

    .icon-archive {
      color: #ff9800;
    }

    .icon-reactivate {
      color: #4caf50;
    }

    .icon-delete {
      color: #f44336;
    }

    h2 {
      margin: 0;
      font-size: 20px;
    }

    .dialog-message {
      margin: 16px 0;
      color: rgba(0, 0, 0, 0.7);
      line-height: 1.5;
    }

    .item-highlight {
      background: #f5f5f5;
      padding: 12px;
      border-radius: 4px;
      margin: 16px 0;
      border-left: 4px solid #2196f3;
    }

    .full-width {
      width: 100%;
      margin-top: 16px;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
      gap: 8px;
    }

    .btn-archive {
      background-color: #ff9800;
      color: white;
    }

    .btn-reactivate {
      background-color: #4caf50;
      color: white;
    }

    .btn-delete {
      background-color: #f44336;
      color: white;
    }
  `]
})
export class ConfirmActionDialogComponent {
  reasonControl = new FormControl('');

  constructor(
    public dialogRef: MatDialogRef<ConfirmActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmActionDialogData
  ) {
    // Add validation if reason is required
    if (data.requiresReason && data.actionType !== 'archive') {
      this.reasonControl.setValidators([Validators.required]);
    }
  }

  getIcon(): string {
    switch (this.data.actionType) {
      case 'archive':
        return 'archive';
      case 'reactivate':
        return 'restore';
      case 'delete':
        return 'delete_forever';
      default:
        return 'help';
    }
  }

  getButtonClass(): string {
    return `btn-${this.data.actionType}`;
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  onConfirm(): void {
    // Validate if required
    if (this.data.requiresReason && this.data.actionType !== 'archive') {
      if (!this.reasonControl.value?.trim()) {
        return;
      }
    }

    this.dialogRef.close({
      confirmed: true,
      reason: this.reasonControl.value || undefined
    });
  }
}